import { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

const PortalNavigationContext = createContext();

export function PortalNavigationProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [lastDashboard, setLastDashboard] = useState(null);

  // Define dashboard routes for each role
  const getDashboardRoute = (role) => {
    const dashboardRoutes = {
      admin: '/admin',
      examiner: '/examiner',
      exam_center: '/dashboard/examination-center',
      examination_center: '/dashboard/examination-center',
      institution: '/dashboard/institutional',
      institutional: '/dashboard/institutional',
      accreditation: '/dashboard/accreditation',
      curriculum: '/dashboard/curriculum',
      candidate: '/dashboard',
      student: '/dashboard'
    };
    return dashboardRoutes[role] || '/dashboard';
  };

  // Track the last dashboard visited
  useEffect(() => {
    if (isAuthenticated && user) {
      const dashboardRoute = getDashboardRoute(user.role);
      // If user is on their dashboard, remember it
      if (location.pathname.includes('dashboard') || location.pathname === '/admin') {
        setLastDashboard(location.pathname);
      }
    }
  }, [location.pathname, isAuthenticated, user]);

  // Handle browser back button - intercept navigation to wrong dashboards
  useEffect(() => {
    const handlePopState = () => {
      if (isAuthenticated && user) {
        const currentPath = window.location.pathname;
        const userDashboard = getDashboardRoute(user.role);
        
        // List of dashboard paths
        const dashboardPaths = [
          '/dashboard', '/admin', '/examiner', 
          '/dashboard/examination-center', '/dashboard/institutional',
          '/dashboard/accreditation', '/dashboard/curriculum', '/dashboard/student'
        ];
        
        // If navigating to a wrong dashboard, redirect to correct one
        const isNavigatingToDashboard = dashboardPaths.some(path => currentPath.startsWith(path));
        
        if (isNavigatingToDashboard && !currentPath.startsWith(userDashboard) && currentPath !== '/apply') {
          // User is trying to go to wrong dashboard
          setTimeout(() => {
            navigate(userDashboard, { replace: true });
          }, 0);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isAuthenticated, user, navigate]);

  // Navigate to user's dashboard
  const goToDashboard = () => {
    if (user) {
      navigate(getDashboardRoute(user.role));
    }
  };

  // Navigate back (with smart fallback to dashboard)
  const goBack = () => {
    if (lastDashboard) {
      navigate(lastDashboard);
    } else if (user) {
      navigate(getDashboardRoute(user.role));
    } else {
      navigate(-1);
    }
  };

  return (
    <PortalNavigationContext.Provider value={{ 
      goToDashboard, 
      goBack, 
      lastDashboard,
      getDashboardRoute 
    }}>
      {children}
    </PortalNavigationContext.Provider>
  );
}

export function usePortalNavigation() {
  const context = useContext(PortalNavigationContext);
  if (!context) {
    throw new Error('usePortalNavigation must be used within PortalNavigationProvider');
  }
  return context;
}
